import {
  Grid2 as Grid,
  Button,
  Skeleton,
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  Stack,
  Box,
  IconButton,
  useTheme,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from "@mui/material";
import React, { useState } from "react";
import AiIcon from "../assets/icons/AiIcon";
import { TaskModel } from "../models/TaskModels";
import { useNavigate, useParams } from "react-router-dom";
import { useGetEmailContent } from "../hooks/useGetEmailContent";
import { getReadingEmailBody, replyEmail } from "../taskpane";
import CloseIcon from "@mui/icons-material/Close";
import { useDraftEmail } from "../hooks/useDraftEmail";
import { useOutlook } from "../hooks/useOutlook";
import { AttachmentModel, EmailResponseModel } from "../models/EmailModels";
import { UserStyleModel } from "../models/StylesModels";
import ActionWithAiContext from "./ActionWithAiContext";
import EmailChat from "./EmailChat";
import InvoicesActions from "./InvoicesActions/InvoicesActions";
import { ActionsList } from "./ActionsList/ActionsList";
import { useEnvironment } from "../hooks/useEnvironment";
import { useCurrentMailbox } from "../hooks/useCurrentMailbox";
import { SearchInvoiceReceivableResponseDetailedModel } from "../models/SearchModels";
import { OrganisationFileResponse } from "../models/OrganisationModels";
import { ChatTypeEnum } from "./ChatType";
import { useUserConfiguration } from "../hooks/useUserConfiguration";
import { normalizeEmailHtml, prepareHtmlForOutlook } from "../utils/emailHtml";
import { useEmailTemplates } from "../hooks/emailTemplates/useEmailTemplates";
import { useDraftEmailTemplate } from "../hooks/emailTemplates/useDraftEmailTemplate";
import { buildTemplateParameters } from "../hooks/emailTemplates/buildTemplateParameters";
import { EmailTemplateResponse } from "../models/EmailTemplateModels";
import EmailIcon from "@mui/icons-material/EmailOutlined";

const ActionWithAi: React.FC<{ actions?: TaskModel[]; relatedIds: string[]; onAct?: () => void }> = ({
  actions,
  relatedIds,
  onAct,
}) => {
  const { supplierId, customerId, invoiceId } = useParams();
  const { isProduction } = useEnvironment();
  const theme = useTheme();
  const { getEmailContent } = useGetEmailContent();
  const { userConfiguration } = useUserConfiguration();
  const { emailTemplates, emailTemplatesMetadata } = useEmailTemplates();
  const { draftEmailTemplate, isDraftEmailTemplateLoading } = useDraftEmailTemplate();
  const {
    draftNewReply,
    isDraftNewReplyLoading,
    updateDraftNewReply,
    isUpdateDraftNewReplyLoading,
    draftReply,
    isDraftReplyLoading,
    updateDraftReply,
    isUpdateDraftReplyLoading,
  } = useDraftEmail();
  const { isInReadMode } = useOutlook();
  const navigate = useNavigate();
  const [openDialog, setOpenDialog] = useState(false);
  const [currentDraft, setCurrentDraft] = useState<string | null>(null);
  const [isDraftingLoading, setIsDraftingLoading] = useState(false);
  const { isMailboxConnected } = useCurrentMailbox();
  const [draftText, setDraftText] = useState("");
  const [promptText, setPromptText] = useState("");
  const [isDraftReady, setIsDraftReady] = useState(false);
  const [apiAttachments, setApiAttachments] = useState<AttachmentModel[]>([]);
  const [chatType, setChatType] = useState<ChatTypeEnum>("Email");

  const resetDraftState = () => {
    setDraftText("");
    setPromptText("");
    setIsDraftReady(false);
    setCurrentDraft(null);
    setApiAttachments([]);
    setChatType("Email");
  };

  const applyDraftResponse = (response: EmailResponseModel, userPrompt: string) => {
    const html = normalizeEmailHtml(response.content);
    setDraftText(html);
    setCurrentDraft(html);
    setPromptText(userPrompt);
    setApiAttachments(response.attachments);
    setIsDraftReady(true);
    setChatType("Email");
  };

  const handleOpenActionMenu = async () => {
    setOpenDialog(true);
  };

  const handleCloseActionMenu = () => {
    setOpenDialog(false);
    resetDraftState();
  };

  const buildEmailAttachments = (
    attachments?: SearchInvoiceReceivableResponseDetailedModel[],
    organisationFiles?: OrganisationFileResponse[]
  ) => {
    const receivableAttachments =
      attachments?.map((att) => ({
        id: att.id,
        name: att.invoiceNumber,
        type: "CustomerInvoice",
      })) ?? [];
    const organisationFileAttachments =
      organisationFiles?.map((f) => ({
        id: f.id,
        name: f.displayName || f.fileName || "file",
        type: "OrganisationFile",
      })) ?? [];
    return [...receivableAttachments, ...organisationFileAttachments];
  };

  const handleActionClick = async (action: TaskModel) => {
    if (action.linkType === "External" && action.link) {
      window.open(action.link, "_blank");
    } else if ((action.linkType === "EmailResponse" || action.linkType === "InvoiceEmailResponse") && action.link) {
      setIsDraftingLoading(true);
      const receivedEmailContent = await getReadingEmailBody();
      const response = await getEmailContent({
        path: action.link,
        relatedIds,
        receivedEmailContent,
      });

      if (isInReadMode) {
        applyDraftResponse(response, action.name);
      } else {
        const emailContent = prepareHtmlForOutlook(response.content);
        await replyEmail(emailContent, response.attachments, userConfiguration?.emailFont || null);
      }
      setIsDraftingLoading(false);
    } else if (action.linkType === "Internal" && action.link) {
      navigate(action.link);
    }
    onAct?.();
  };

  const handleEmailTemplateClick = async (template: EmailTemplateResponse) => {
    const metadata = emailTemplatesMetadata?.find((item) => item.templateType === template.type);
    const invoiceIds = relatedIds.length > 0 ? relatedIds : invoiceId ? [invoiceId] : [];
    const templateParameters = buildTemplateParameters(metadata?.templateProperties ?? [], {
      relatedIds: invoiceIds,
      customerId,
    });

    setIsDraftingLoading(true);
    const response = await draftEmailTemplate({
      templateId: template.id,
      templateParameters,
    });

    if (response) {
      if (isInReadMode) {
        applyDraftResponse(response, template.name);
      } else {
        const emailContent = prepareHtmlForOutlook(response.content);
        await replyEmail(emailContent, response.attachments, userConfiguration?.emailFont || null);
      }
    }
    setIsDraftingLoading(false);
    onAct?.();
  };

  const handleDraftEmail = async (
    message: string,
    currentStyle: UserStyleModel | null,
    _type: ChatTypeEnum,
    attachments?: SearchInvoiceReceivableResponseDetailedModel[],
    organisationFiles?: OrganisationFileResponse[],
    isReplyAll?: boolean,
    currentLanguage?: string
  ) => {
    let receivedEmailContent: EmailResponseModel | null = null;
    const emailAttachments = buildEmailAttachments(attachments, organisationFiles);

    if (isInReadMode) {
      setIsDraftingLoading(true);
      const emailContent = await getReadingEmailBody();
      if (!currentDraft) {
        receivedEmailContent = await draftReply({
          userPrompt: message,
          receivedEmailContent: emailContent,
          styleId: currentStyle?.id,
          language: currentLanguage,
          attachments: emailAttachments,
        });
      } else {
        receivedEmailContent = await updateDraftReply({
          userPrompt: message,
          currentDraft: currentDraft,
          receivedEmailContent: emailContent,
          styleId: currentStyle?.id,
          language: currentLanguage,
          attachments: emailAttachments,
        });
      }

      if (receivedEmailContent) {
        applyDraftResponse(receivedEmailContent, message);
      }
      setIsDraftingLoading(false);
      return;
    }

    if (!currentDraft) {
      receivedEmailContent = await draftNewReply({
        userPrompt: message,
        styleId: currentStyle?.id,
        language: currentLanguage,
        attachments: emailAttachments,
      });
    } else {
      receivedEmailContent = await updateDraftNewReply({
        userPrompt: message,
        currentDraft: currentDraft,
        styleId: currentStyle?.id,
        language: currentLanguage,
        attachments: emailAttachments,
      });
    }

    const emailContent = prepareHtmlForOutlook(receivedEmailContent.content);

    setCurrentDraft(emailContent);

    await replyEmail(emailContent, receivedEmailContent.attachments, userConfiguration?.emailFont || null, isReplyAll);
    onAct?.();
  };

  const handleRedraft = async (
    prompt: string,
    draftHtml: string,
    currentStyle: UserStyleModel | null,
    _type: ChatTypeEnum,
    attachments?: SearchInvoiceReceivableResponseDetailedModel[],
    organisationFiles?: OrganisationFileResponse[],
    _isReplyAll?: boolean,
    currentLanguage?: string
  ) => {
    if (!isInReadMode) return;

    setIsDraftingLoading(true);
    const emailContent = await getReadingEmailBody();
    const emailAttachments = buildEmailAttachments(attachments, organisationFiles);

    const receivedEmailContent = await updateDraftReply({
      userPrompt: prompt,
      currentDraft: draftHtml,
      receivedEmailContent: emailContent,
      styleId: currentStyle?.id,
      language: currentLanguage,
      attachments: emailAttachments,
    });

    if (receivedEmailContent) {
      applyDraftResponse(receivedEmailContent, prompt);
    }
    setIsDraftingLoading(false);
  };

  const handleSendDraft = async (
    draftHtml: string,
    _currentStyle: UserStyleModel | null,
    _type: ChatTypeEnum,
    _attachments?: SearchInvoiceReceivableResponseDetailedModel[],
    _organisationFiles?: OrganisationFileResponse[],
    isReplyAll?: boolean
  ) => {
    const finalEmailContent = prepareHtmlForOutlook(draftHtml);
    await replyEmail(finalEmailContent, apiAttachments, userConfiguration?.emailFont || null, isReplyAll);
    onAct?.();
    resetDraftState();
  };

  if (!actions) return;

  const emailActions = actions.filter(
    (action) => action.linkType === "EmailResponse" || action.linkType === "InvoiceEmailResponse"
  );
  const otherActions = actions.filter(
    (action) => action.linkType !== "EmailResponse" && action.linkType !== "InvoiceEmailResponse"
  );

  const isLoading =
    isDraftNewReplyLoading ||
    isDraftReplyLoading ||
    isUpdateDraftNewReplyLoading ||
    isUpdateDraftReplyLoading ||
    isDraftEmailTemplateLoading;

  return (
    <>
      <Dialog
        open={openDialog}
        onClose={handleCloseActionMenu}
        fullScreen
        sx={{
          "& .MuiDialog-paper": {
            background: theme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ padding: "5px 24px" }}>
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Typography
              variant="h6"
              sx={{
                fontSize: "1.25rem",
                fontWeight: "bold",
                color: theme.palette.text.primary,
              }}
            >
              Actions
            </Typography>
            <IconButton onClick={handleCloseActionMenu} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack direction="column" spacing={2} height="100%">
            <Typography variant="body1">
              Choose an action, or switch between Email mode to draft responses and Chat mode to have a conversation
              with Attelas AI.
            </Typography>
            <ActionWithAiContext />
            <Box flexGrow={1}>
              {emailActions.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ marginTop: 2, fontWeight: "bold" }}>
                    Draft
                  </Typography>
                  <Stack direction="column" spacing={1}>
                    <ActionsList>
                      {emailActions.map((action) => (
                        <ListItemButton
                          key={action.name}
                          onClick={() => handleActionClick(action)}
                          component="li"
                          disabled={isLoading || isDraftingLoading}
                        >
                          <ListItemIcon>
                            <AiIcon />
                          </ListItemIcon>
                          <ListItemText primary={action.name} />
                        </ListItemButton>
                      ))}
                    </ActionsList>
                  </Stack>
                </>
              )}

              {isInReadMode && !isProduction && supplierId && isMailboxConnected && (
                <>
                  <Typography variant="subtitle1" sx={{ marginTop: 2, fontWeight: "bold" }}>
                    Workflow
                  </Typography>
                  <Stack direction="column" spacing={1}>
                    <InvoicesActions />
                  </Stack>
                </>
              )}

              {otherActions.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ marginTop: 2, fontWeight: "bold" }}>
                    Suggested
                  </Typography>
                  <Stack direction="column" spacing={1}>
                    <ActionsList>
                      {otherActions.map((action) => (
                        <ListItemButton
                          key={action.name}
                          onClick={() => handleActionClick(action)}
                          component="li"
                          disabled={isLoading || isDraftingLoading}
                        >
                          <ListItemIcon>
                            <AiIcon />
                          </ListItemIcon>
                          <ListItemText primary={action.name} />
                        </ListItemButton>
                      ))}
                    </ActionsList>
                  </Stack>
                </>
              )}
              {emailTemplates && emailTemplates.length > 0 && (
                <>
                  <Typography variant="subtitle1" sx={{ marginTop: 2, fontWeight: "bold" }}>
                    Email Templates
                  </Typography>
                  <Stack direction="column" spacing={1}>
                    <ActionsList>
                      {emailTemplates
                        .filter((template) => template.type === "Standard")
                        .map((template) => (
                          <ListItemButton
                            key={template.id}
                            component="li"
                            onClick={() => handleEmailTemplateClick(template)}
                            disabled={isLoading || isDraftingLoading}
                          >
                            <ListItemIcon>
                              <EmailIcon />
                            </ListItemIcon>
                            <ListItemText primary={template.name} />
                          </ListItemButton>
                        ))}
                    </ActionsList>
                  </Stack>
                </>
              )}
            </Box>
            <EmailChat
              onSendMessage={handleDraftEmail}
              onRedraft={isInReadMode ? handleRedraft : undefined}
              onSendDraft={isInReadMode ? handleSendDraft : undefined}
              isLoading={isLoading || isDraftingLoading}
              placeholder="Write an email regarding ..."
              maxLength={255}
              promptValue={isInReadMode ? promptText : undefined}
              onPromptChange={isInReadMode ? setPromptText : undefined}
              draftValue={isInReadMode ? draftText : undefined}
              onDraftChange={isInReadMode ? setDraftText : undefined}
              isDraftReady={isInReadMode && isDraftReady}
              chatType={isInReadMode ? chatType : undefined}
              onChatTypeChange={isInReadMode ? setChatType : undefined}
            />
          </Stack>
        </DialogContent>
      </Dialog>
      <Grid size="grow" height="100%">
        <Button
          fullWidth
          variant="outlined"
          endIcon={<AiIcon />}
          onClick={handleOpenActionMenu}
          sx={{ height: "100%" }}
        >
          {actions ? "Actions" : <Skeleton variant="text" width={50} height={20} />}
        </Button>
      </Grid>
    </>
  );
};

export default ActionWithAi;
